import React, { useEffect, useState, useCallback } from 'react';
import { Card } from '../components/ui/card';
import { History, User, Clock, RefreshCw, LogIn, LogOut, ShieldCheck, ShieldAlert, ChevronLeft, ChevronRight } from 'lucide-react';
import { usePeriod } from '../lib/period';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export default function RiwayatAktivitas() {
  // Ambil role akun dari localStorage dengan berbagai kemungkinan format
  let userRole = localStorage.getItem('role') || '';

  if (!userRole) {
    try {
      const userObj = JSON.parse(localStorage.getItem('user') || '{}');
      userRole = userObj.role || userObj.user?.role || '';
    } catch (e) {
      userRole = '';
    }
  }

  if (!userRole) {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('access_token');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        userRole = payload.role || '';
      }
    } catch (e) {}
  }

  const roleLower = String(userRole).toLowerCase().trim();
  // Hanya blokir jika role terbukti 'user'
  const isDenied = roleLower === 'user';

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // State Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const { year, month } = usePeriod();
  const periodStr = `${year}-${String(month).padStart(2, '0')}`;

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/history?period=${periodStr}`);
      if (res.ok) {
        const data = await res.json();
        setLogs(data);
        setCurrentPage(1);
      }
    } catch (err) {
      console.error('Gagal mengambil riwayat:', err);
    } finally {
      setLoading(false);
    }
  }, [periodStr]);

  useEffect(() => {
    if (!isDenied) {
      fetchHistory();
    }
  }, [fetchHistory, isDenied]);

  // Kalkulasi Pagination
  const totalPages = Math.ceil(logs.length / itemsPerPage) || 1;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentLogs = logs.slice(indexOfFirstItem, indexOfLastItem);

  const getActionBadge = (action) => {
    const act = (action || '').toUpperCase();
    if (act.includes('LOGIN')) {
      return (
        <span className="inline-flex items-center gap-1 rounded-md bg-blue-50 px-2.5 py-1 text-[11px] font-semibold text-blue-700 dark:bg-blue-950/40 dark:text-blue-400 border border-blue-200/50 dark:border-blue-900/50">
          <LogIn className="h-3 w-3" /> {action}
        </span>
      );
    }
    if (act.includes('LOGOUT')) {
      return (
        <span className="inline-flex items-center gap-1 rounded-md bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-700 dark:bg-amber-950/40 dark:text-amber-400 border border-amber-200/50 dark:border-amber-900/50">
          <LogOut className="h-3 w-3" /> {action}
        </span>
      );
    }
    if (act.includes('HAPUS') || act.includes('RESEED')) {
      return (
        <span className="inline-flex items-center rounded-md bg-red-50 px-2.5 py-1 text-[11px] font-semibold text-red-700 dark:bg-red-950/40 dark:text-red-400 border border-red-200/50 dark:border-red-900/50">
          {action}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center rounded-md bg-teal-50 px-2.5 py-1 text-[11px] font-semibold text-teal-700 dark:bg-teal-950/40 dark:text-teal-400 border border-teal-200/50 dark:border-teal-900/50">
        {action}
      </span>
    );
  };

  // Tampilan Hanya untuk 'User' Biasa
  if (isDenied) {
    return (
      <div className="max-w-6xl space-y-6">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-slate-900 dark:text-white flex items-center gap-2">
            <History className="h-6 w-6 text-teal-600" /> Riwayat Aktivitas Sistem
          </h1>
          <p className="text-sm text-slate-500 mt-1">Log aktivitas dan rekam jejak tindakan pengguna.</p>
        </div>

        <Card className="p-12 text-center border dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900">
          <div className="flex flex-col items-center justify-center space-y-3">
            <div className="p-3 bg-red-50 dark:bg-red-950/40 rounded-full text-red-600 dark:text-red-400 border border-red-200/50 dark:border-red-900/50">
              <ShieldAlert className="h-10 w-10" />
            </div>
            <h2 className="text-lg font-bold text-slate-800 dark:text-white">Tidak Ada Akses</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 max-w-md">
              Maaf, Anda tidak memiliki hak akses untuk melihat Riwayat Aktivitas Sistem. Fitur ini khusus dibatasi untuk **Admin** dan **Koordinator**.
            </p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-6xl space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-slate-900 dark:text-white flex items-center gap-2">
            <History className="h-6 w-6 text-teal-600" /> Riwayat Aktivitas Sistem ({periodStr})
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Log aktivitas, rekam jejak login/logout, dan tindakan seluruh pengguna untuk periode bulan ini.
          </p>
        </div>
        <button
          onClick={fetchHistory}
          className="flex items-center gap-2 rounded-lg border bg-white px-3.5 py-2 text-xs font-medium text-slate-700 shadow-sm hover:bg-slate-50 dark:bg-slate-900 dark:border-slate-800 dark:text-slate-300 dark:hover:bg-slate-800"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Muat Ulang
        </button>
      </div>

      <Card className="overflow-hidden p-0 border dark:border-slate-800 shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="border-b bg-slate-50 text-slate-700 dark:bg-slate-800/80 dark:text-slate-300">
              <tr>
                <th className="p-3.5 font-semibold">Waktu (WIB)</th>
                <th className="p-3.5 font-semibold">Pengguna</th>
                <th className="p-3.5 font-semibold">Aksi / Tindakan</th>
                <th className="p-3.5 font-semibold">Keterangan Detail</th>
              </tr>
            </thead>
            <tbody className="divide-y dark:divide-slate-800">
              {loading ? (
                <tr>
                  <td colSpan="4" className="p-6 text-center text-slate-500">Memuat data riwayat...</td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan="4" className="p-6 text-center text-slate-500">
                    Belum ada riwayat aktivitas yang tercatat untuk periode {periodStr}.
                  </td>
                </tr>
              ) : (
                currentLogs.map((log) => (
                  <tr key={log.id || log._id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/40 transition-colors">
                    <td className="p-3.5 whitespace-nowrap text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
                      <Clock className="h-3.5 w-3.5 text-slate-400" />
                      {new Date(log.created_at).toLocaleString('id-ID', {
                        dateStyle: 'medium',
                        timeStyle: 'medium'
                      })}
                    </td>
                    <td className="p-3.5 font-medium whitespace-nowrap text-slate-900 dark:text-white">
                      <div className="flex items-center gap-2">
                        <User className="h-3.5 w-3.5 text-teal-600" />
                        <span>{log.username}</span>
                        <span className="text-[10px] rounded-full bg-slate-100 px-2 py-0.5 text-slate-600 dark:bg-slate-800 dark:text-slate-400 flex items-center gap-1">
                          <ShieldCheck className="h-2.5 w-2.5 text-teal-500" /> {log.role}
                        </span>
                      </div>
                    </td>
                    <td className="p-3.5 whitespace-nowrap">
                      {getActionBadge(log.action)}
                    </td>
                    <td className="p-3.5 text-slate-600 dark:text-slate-300 max-w-md truncate" title={log.details}>
                      {log.details}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* NAVIGASI PAGINATION */}
        {!loading && logs.length > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t bg-slate-50/50 dark:bg-slate-800/50 text-xs text-slate-600 dark:text-slate-400">
            <span>
              Menampilkan {indexOfFirstItem + 1} - {Math.min(indexOfLastItem, logs.length)} dari Total <b>{logs.length}</b> Log
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="p-1.5 rounded border bg-white dark:bg-slate-900 disabled:opacity-40 hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="px-2 font-medium">
                Halaman {currentPage} dari {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="p-1.5 rounded border bg-white dark:bg-slate-900 disabled:opacity-40 hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
